/**
 * The subscribe dialog over a working app (TMMAPP-0116): `offer` mode for a guest, `dismissible`
 * for a lapsed subscriber in grace. Every purchase entry point (the Subscribe card, a
 * subscriber-only action, Settings' Renew, the own-books page) calls `useOpenSubscribe()`; the
 * blocking dialog is the entry gate's, not this.
 */
import { createContext, useContext } from 'react';
import type { SubscribeGate } from '@/session/subscribeGate';

export interface SubscribeOfferValue {
  openSubscribe: () => void;
  /** The dialog is up: the guest tour waits until it closes. */
  dialogOpen: boolean;
}

export const SubscribeOfferContext = createContext<SubscribeOfferValue | null>(null);

/** The entry gate's decision for the routes it lets through (`open` or `grace`). */
export const SubscribeGateContext = createContext<SubscribeGate>('open');

/** Navigation state that opens `offer` mode on arrival: an invitation just accepted. */
export const SUBSCRIBE_OFFER_STATE = { subscribeOffer: true } as const;

export function hasSubscribeOfferState(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { subscribeOffer?: unknown }).subscribeOffer === true
  );
}

const noop = () => undefined;

/** Opens the dialog in the mode the tier calls for. A no-op outside the shell. */
export function useOpenSubscribe(): () => void {
  return useContext(SubscribeOfferContext)?.openSubscribe ?? noop;
}

export function useSubscribeDialogOpen(): boolean {
  return useContext(SubscribeOfferContext)?.dialogOpen ?? false;
}
